import { AddressSearchInput } from '@/components/global/address/AddressSearchInput';
import { GoogleMapsProvider } from '@/components/global/address/GoogleMapsProviderSingleton';
import { CountryCombobox } from '@/components/ui/country-combobox';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/shadcn/command';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { useAdaptiveDebounce } from '@/hooks/useAdaptiveDebounce';
import { useTranslations } from '@/hooks/useTranslations';
import { Form, Head } from '@inertiajs/react';
import { Building2, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface Country {
    value: string;
    label: string;
}

interface InitialSetupProps {
    countries: Country[];
}

interface CompanySearchResult {
    code: string;
    name: string;
    address: string | null;
    vat_number: string | null;
    phone: string | null;
    email: string | null;
}

export default function InitialSetup({ countries }: InitialSetupProps) {
    const { t } = useTranslations();

    // Normalize countries to ensure proper format
    const normalizedCountries = useMemo(() => {
        if (!Array.isArray(countries)) return [];
        return countries
            .map((country) => {
                // If it's already an object with value and label, return it
                if (typeof country === 'object' && country !== null && 'value' in country && 'label' in country) {
                    return country as Country;
                }
                // If it's a string, convert it to the expected format
                if (typeof country === 'string') {
                    return { value: country, label: country };
                }
                // Otherwise, skip invalid entries
                return null;
            })
            .filter((c): c is Country => c !== null);
    }, [countries]);

    const [homeCountry, setHomeCountry] = useState<string>('LT');
    const [companyAddress, setCompanyAddress] = useState<string>('');
    // Company search state
    const [companySearchOpen, setCompanySearchOpen] = useState(false);
    const [companySearchTerm, setCompanySearchTerm] = useState('');
    const [companySearchResults, setCompanySearchResults] = useState<CompanySearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Form field states for auto-fill
    const [companyName, setCompanyName] = useState('');
    const [companyCode, setCompanyCode] = useState('');
    const [vatNumber, setVatNumber] = useState('');
    const [companyPhone, setCompanyPhone] = useState('');
    const [companyEmail, setCompanyEmail] = useState('');

    const debouncedSearchTerm = useAdaptiveDebounce(companySearchTerm, {
        baseDelay: 300,
        fastTypingDelay: 500,
        typingThreshold: 150,
    });

    // Search company registry
    const searchCompanyRegistry = useCallback(
        async (query: string) => {
            if (query.length < 2) {
                setCompanySearchResults([]);
                return;
            }

            setIsSearching(true);
            try {
                const response = await fetch(`/setup/initial/search-companies?q=${encodeURIComponent(query)}`, {
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    credentials: 'same-origin',
                });

                if (!response.ok) {
                    throw new Error('Failed to search companies');
                }

                const data = await response.json();
                setCompanySearchResults(data);
            } catch (error) {
                console.error('Company search error:', error);
                toast.error(t('toasts.settings_initialsetup.search_failed', 'Failed to search companies. Please try again.'));
                setCompanySearchResults([]);
            } finally {
                setIsSearching(false);
            }
        },
        [t],
    );

    // Effect to trigger search when debounced term changes
    useEffect(() => {
        if (debouncedSearchTerm) {
            searchCompanyRegistry(debouncedSearchTerm);
        } else {
            setCompanySearchResults([]);
        }
    }, [debouncedSearchTerm, searchCompanyRegistry]);

    // Handle company selection from search
    const handleCompanySelect = (company: CompanySearchResult) => {
        setCompanyName(company.name);
        setCompanyCode(company.code);
        setVatNumber(company.vat_number || '');
        setCompanyAddress(company.address || '');
        setCompanyPhone(company.phone || '');
        setCompanyEmail(company.email || '');

        setCompanySearchOpen(false);
        setCompanySearchTerm('');

        toast.success(t('toasts.settings_initialsetup.company_loaded', 'Company details loaded from registry'));
    };

    return (
        <>
            <Head title="Initial System Setup" />

            <div className="bg-muted flex min-h-screen items-center justify-center p-4">
                <Card className="w-full max-w-3xl shadow-lg">
                    <CardHeader>
                        <div className="flex items-center gap-4">
                            <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-lg">
                                <Building2 className="text-primary h-6 w-6" />
                            </div>
                            <div>
                                <CardTitle className="text-2xl">Welcome to Your CRM</CardTitle>
                                <CardDescription>Please complete the initial system setup to get started</CardDescription>
                            </div>
                        </div>
                    </CardHeader>

                    <CardContent>
                        <Form action={route('setup.initial.store')} method="post" className="space-y-6">
                            {({ errors, processing }) => (
                                <>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold">System Configuration</h3>
                                            <p className="text-muted-foreground text-sm">Configure your system's default settings</p>
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="homeCountry">
                                                Home Country <span className="text-destructive">*</span>
                                            </Label>
                                            <input type="hidden" name="homeCountry" value={homeCountry} />
                                            <CountryCombobox
                                                countries={normalizedCountries}
                                                value={homeCountry}
                                                onValueChange={setHomeCountry}
                                                placeholder="Select your country"
                                                searchPlaceholder="Search country..."
                                                required
                                            />
                                            {errors.homeCountry && <p className="text-destructive text-sm">{errors.homeCountry}</p>}
                                            <p className="text-muted-foreground text-xs">Select the primary country where your company operates</p>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <h3 className="text-lg font-semibold">Company Information</h3>
                                            <p className="text-muted-foreground text-sm">Your company details for invoices and documents</p>
                                        </div>

                                        <div className="grid gap-4 md:grid-cols-2">
                                            {/* Company Registry Search */}
                                            <div className="space-y-2 md:col-span-2">
                                                <Label>Search Company Registry (Optional)</Label>
                                                <Popover open={companySearchOpen} onOpenChange={setCompanySearchOpen}>
                                                    <PopoverTrigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            role="combobox"
                                                            aria-expanded={companySearchOpen}
                                                            className="w-full justify-between"
                                                            type="button"
                                                        >
                                                            <span className="flex items-center gap-2">
                                                                <Search className="h-4 w-4" />
                                                                Search for your company...
                                                            </span>
                                                        </Button>
                                                    </PopoverTrigger>
                                                    <PopoverContent
                                                        className="w-[var(--radix-popover-trigger-width)] p-0"
                                                        align="start"
                                                        sideOffset={4}
                                                    >
                                                        <Command shouldFilter={false} className="w-full">
                                                            <CommandInput
                                                                placeholder="Search by company name or code..."
                                                                value={companySearchTerm}
                                                                onValueChange={setCompanySearchTerm}
                                                            />
                                                            <CommandList>
                                                                {isSearching ? (
                                                                    <CommandEmpty>Searching...</CommandEmpty>
                                                                ) : companySearchResults.length === 0 ? (
                                                                    <CommandEmpty>
                                                                        {companySearchTerm.length < 2
                                                                            ? 'Type at least 2 characters to search'
                                                                            : 'No companies found'}
                                                                    </CommandEmpty>
                                                                ) : (
                                                                    <CommandGroup>
                                                                        {companySearchResults.map((company) => (
                                                                            <CommandItem
                                                                                key={company.code}
                                                                                value={company.code}
                                                                                onSelect={() => handleCompanySelect(company)}
                                                                            >
                                                                                <div className="flex flex-col">
                                                                                    <span className="font-medium">{company.name}</span>
                                                                                    <span className="text-muted-foreground text-xs">
                                                                                        Code: {company.code}
                                                                                        {company.address && ` • ${company.address}`}
                                                                                    </span>
                                                                                </div>
                                                                            </CommandItem>
                                                                        ))}
                                                                    </CommandGroup>
                                                                )}
                                                            </CommandList>
                                                        </Command>
                                                    </PopoverContent>
                                                </Popover>
                                                <p className="text-muted-foreground text-xs">
                                                    Search and auto-fill company details from the registry, or enter manually below
                                                </p>
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="company_name">
                                                    Company Name <span className="text-destructive">*</span>
                                                </Label>
                                                <Input
                                                    id="company_name"
                                                    name="company_name"
                                                    placeholder="Your Company Ltd."
                                                    required
                                                    disabled={processing}
                                                    value={companyName}
                                                    onChange={(e) => setCompanyName(e.target.value)}
                                                />
                                                {errors.company_name && <p className="text-destructive text-sm">{errors.company_name}</p>}
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="company_code">
                                                    Company Code <span className="text-destructive">*</span>
                                                </Label>
                                                <Input
                                                    id="company_code"
                                                    name="company_code"
                                                    placeholder="123456789"
                                                    required
                                                    disabled={processing}
                                                    value={companyCode}
                                                    onChange={(e) => setCompanyCode(e.target.value)}
                                                />
                                                {errors.company_code && <p className="text-destructive text-sm">{errors.company_code}</p>}
                                            </div>

                                            <div className="space-y-2 md:col-span-2">
                                                <Label htmlFor="company_address">
                                                    Company Address <span className="text-destructive">*</span>
                                                </Label>
                                                <input type="hidden" name="company_address" value={companyAddress} required />
                                                <GoogleMapsProvider>
                                                    <AddressSearchInput
                                                        value={companyAddress}
                                                        onChange={setCompanyAddress}
                                                        onAddressSelect={(address) => setCompanyAddress(address.label)}
                                                        countryCode={homeCountry.toLowerCase()}
                                                        placeholder="Search company address"
                                                        className="w-full"
                                                    />
                                                </GoogleMapsProvider>
                                                {errors.company_address && <p className="text-destructive text-sm">{errors.company_address}</p>}
                                                <p className="text-muted-foreground text-xs">
                                                    Search and select an address using Google autocomplete
                                                </p>
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="vat_number">VAT Number</Label>
                                                <Input
                                                    id="vat_number"
                                                    name="vat_number"
                                                    placeholder="LT123456789"
                                                    disabled={processing}
                                                    value={vatNumber}
                                                    onChange={(e) => setVatNumber(e.target.value)}
                                                />
                                                {errors.vat_number && <p className="text-destructive text-sm">{errors.vat_number}</p>}
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="phone">
                                                    Phone <span className="text-destructive">*</span>
                                                </Label>
                                                <Input
                                                    id="phone"
                                                    name="phone"
                                                    placeholder="+1 234 567 8900"
                                                    required
                                                    disabled={processing}
                                                    value={companyPhone}
                                                    onChange={(e) => setCompanyPhone(e.target.value)}
                                                />
                                                {errors.phone && <p className="text-destructive text-sm">{errors.phone}</p>}
                                            </div>

                                            <div className="space-y-2 md:col-span-2">
                                                <Label htmlFor="email">
                                                    Email <span className="text-destructive">*</span>
                                                </Label>
                                                <Input
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    placeholder="info@company.com"
                                                    required
                                                    disabled={processing}
                                                    value={companyEmail}
                                                    onChange={(e) => setCompanyEmail(e.target.value)}
                                                />
                                                {errors.email && <p className="text-destructive text-sm">{errors.email}</p>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-4 pt-6">
                                        <Button type="submit" disabled={processing} size="lg">
                                            {processing ? 'Setting up...' : 'Complete Setup'}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </Form>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}
